import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  useTheme,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Avatar,
  ListItemAvatar,
  Tooltip,
  Stack,
  alpha,
} from '@mui/material';
import {
  Message as MessageIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  ShoppingBag as ProductIcon,
  AccessTime as TimeIcon,
  AttachMoney as PriceIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import { UserService } from '../../services/userService';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '../../store/uiStore';
import { MessageService } from '../../services/messageService';
import { getCachedProductThumbnailSrc } from '../../utils/productThumbnailCache';
import { StorageService } from '../../services/storageService';
import { cacheProductThumbnail } from '../../utils/productThumbnailCache';

interface Conversation {
  id: string;
  user1_id: string;
  user2_id: string;
  created_at: string;
  updated_at: string;
  subject?: string;
  product_id?: string;
  last_message_id?: string;
  message_count?: number;
  // Joined data
  user1?: {
    id: string;
    alias?: string;
    name?: string;
    avatar?: string;
  };
  user2?: {
    id: string;
    alias?: string;
    name?: string;
    avatar?: string;
  };
  other_user?: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    alias?: string;
  };
  product?: {
    id: string;
    name: string;
    price?: number;
    image_url?: string;
    images?: string[];
  };
  last_message?: {
    id: string;
    content: string;
    created_at: string;
    sender_id: string;
  };
}

interface MessagesSidebarProps {
  open: boolean;
  onClose: () => void;
}

const MessagesSidebar: React.FC<MessagesSidebarProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { pendingConversationTarget, setPendingConversationTarget } = useUIStore();
  const [userAlias, setUserAlias] = useState<string | null>(null);
  const [loadingAlias, setLoadingAlias] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [highlightConversationId, setHighlightConversationId] = useState<string | undefined>(undefined);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [pendingProductThumb, setPendingProductThumb] = useState<string | null>(null);

  const normalizeProductImageSrc = (src: string | null | undefined): string | null => {
    const s = String(src || '').trim();
    if (!s) return null;
    if (s.startsWith('data:')) return s;
    if (s.startsWith('http://') || s.startsWith('https://')) return s;

    // Handle values that are stored as Supabase Storage object paths, e.g. "<userId>/<file>.jpg"
    return StorageService.getPublicUrl('product-images', s);
  };

  // Helper to get chat partner info
  const getChatPartner = (conversation: Conversation) => {
    // First try to use other_user field (from MessageService)
    if (conversation.other_user) {
      return {
        id: conversation.other_user.id,
        alias: conversation.other_user.alias,
        name: `${conversation.other_user.firstName} ${conversation.other_user.lastName}`.trim(),
        avatar: conversation.other_user.avatar,
      };
    }
    
    // Fallback to user1/user2 fields
    if (user?.id === conversation.user1_id) {
      return conversation.user2;
    }
    return conversation.user1;
  };

  // Helper to format time
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Fetch user alias and conversations when sidebar opens
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id || !open) return;

      try {
        setLoadingAlias(true);
        setLoadingConversations(true);
        
        // Fetch user alias
        const alias = await UserService.getCurrentUserAlias(user.id);
        setUserAlias(alias);

        const serviceConversations = await MessageService.getConversations();
        const mapped: Conversation[] = await Promise.all((serviceConversations || []).map(async (c: any) => {
          const productId = String(c.product_id || c.product?.id || '').trim();
          const productImages: string[] = Array.isArray(c.product?.images) ? c.product.images : [];
          const productFirst = productImages.length > 0 ? productImages[0] : undefined;
          const normalizedFirst = normalizeProductImageSrc(productFirst);
          if (productId && normalizedFirst) {
            await cacheProductThumbnail(productId, normalizedFirst);
          }

          const otherId = String(c.other_user?.id || '').trim();
          let resolvedOtherAlias: string | undefined = c.other_user?.alias;
          if (otherId && (!resolvedOtherAlias || String(resolvedOtherAlias).trim().length === 0)) {
            try {
              const aliasSvc = await UserService.getCurrentUserAlias(otherId);
              if (aliasSvc && String(aliasSvc).trim().length > 0) resolvedOtherAlias = String(aliasSvc);
            } catch {}
          }

          return {
            id: c.id,
            user1_id: c.user1_id,
            user2_id: c.user2_id,
            created_at: c.created_at,
            updated_at: c.updated_at,
            subject: c.subject,
            product_id: c.product_id,
            last_message_id: c.last_message_id,
            message_count: typeof c.message_count === 'number' ? c.message_count : undefined,
            other_user: c.other_user ? { ...c.other_user, alias: resolvedOtherAlias } : c.other_user,
            product: c.product ? {
              id: c.product.id,
              name: c.product.name,
              price: c.product.price,
              images: productImages,
              image_url: normalizedFirst || undefined,
            } : undefined,
            last_message: c.last_message ? {
              id: c.last_message.id,
              content: c.last_message.content,
              created_at: c.last_message.created_at,
              sender_id: c.last_message.sender_id,
            } : undefined,
            user1: undefined,
            user2: undefined,
          };
        }));

        setConversations(mapped);

        // If we have a pending target from Product page, ensure conversation exists
        if (pendingConversationTarget) {
          try {
            // First check if conversation already exists
            const existingConversation = mapped.find(
              conv => conv.other_user?.id === pendingConversationTarget.receiverId && 
                      conv.product_id === pendingConversationTarget.productId &&
                      !(conv as any).offer_id
            );
            
            if (existingConversation) {
              console.log('MessagesSidebar - Found existing conversation:', existingConversation.id);
              setHighlightConversationId(existingConversation.id);
              // Navigate to messages page with the existing conversation ID
              navigate(`/messages?conversation=${existingConversation.id}`);
              // Close the sidebar
              onClose();
              // Clear the target once handled
              setPendingConversationTarget(undefined);
              return;
            }
            
            // If no existing conversation, create a new one
            const subject = pendingConversationTarget.subject || 'Inquiry';
            const conversation = await MessageService.createConversationWithProduct(
              pendingConversationTarget.receiverId,
              pendingConversationTarget.productId,
              subject
            );

            // Fetch user details for the other participant
            const otherUserId = conversation.user1_id === user.id ? conversation.user2_id : conversation.user1_id;
            let otherUser: Conversation['user1'] | undefined = undefined;
            
            try {
              const { data: userData, error: userError } = await supabase
                .from('users')
                .select('id, first_name, last_name, avatar, alias')
                .eq('id', otherUserId)
                .single();
              
              if (userError && userError.code === 'PGRST116') {
                // If RLS blocks the query, try to get alias via service
                const alias = await UserService.getCurrentUserAlias(otherUserId);
                otherUser = { 
                  id: otherUserId, 
                  alias: alias || undefined,
                  name: 'User',
                  avatar: undefined
                };
              } else if (userData) {
                otherUser = {
                  id: userData.id,
                  alias: userData.alias || undefined,
                  name: userData.first_name ? `${userData.first_name} ${userData.last_name || ''}`.trim() : undefined,
                  avatar: userData.avatar || undefined,
                };
              }
            } catch (e) {
              console.warn('Failed to fetch user details for', otherUserId, e);
              otherUser = { id: otherUserId, alias: undefined, name: 'User' };
            }

            // Fetch product details for the new conversation
            let product: Conversation['product'] | undefined = undefined;
            if (conversation.product_id) {
              try {
                const { data: prod } = await supabase
                  .from('products')
                  .select('id, name, price, images')
                  .eq('id', conversation.product_id)
                  .single();
                if (prod) {
                  const images: string[] = Array.isArray((prod as any).images) ? (prod as any).images : [];
                  const first = images.length > 0 ? images[0] : undefined;
                  product = { 
                    id: prod.id, 
                    name: (prod as any).name, 
                    price: (prod as any).price,
                    images,
                    image_url: normalizeProductImageSrc(first) || undefined,
                  };
                }
              } catch (e) {
                console.warn('Failed to fetch product details for', conversation.product_id, e);
              }
            }

            // Create enriched conversation object
            const enrichedConversation: Conversation = {
              ...conversation,
              user1: conversation.user1_id === user.id ? { id: user.id } : otherUser,
              user2: conversation.user2_id === user.id ? { id: user.id } : otherUser,
              product: product,
              // Also handle the other_user field from MessageService
              other_user: conversation.other_user || {
                id: otherUserId,
                firstName: otherUser?.name?.split(' ')[0] || 'User',
                lastName: otherUser?.name?.split(' ').slice(1).join(' ') || '',
                avatar: otherUser?.avatar,
                alias: otherUser?.alias,
              },
            };

            // Add to list if not already
            setConversations((prev) => {
              const exists = prev.some((c) => c.id === conversation.id);
              if (exists) return prev;
              return [enrichedConversation, ...prev];
            });
            setHighlightConversationId(conversation.id);
            
            // Navigate to messages page with the conversation ID
            navigate(`/messages?conversation=${conversation.id}`);
            // Close the sidebar
            onClose();
          } catch (e) {
            console.error('Failed to ensure conversation exists:', e);
          } finally {
            // Clear the target once handled
            setPendingConversationTarget(undefined);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoadingAlias(false);
        setLoadingConversations(false);
      }
    };

    fetchUserData();
  }, [user?.id, open, pendingConversationTarget]);

  useEffect(() => {
    if (!open) {
      setPendingProductThumb(null);
      return;
    }
    const pid = String(pendingConversationTarget?.productId || '').trim();
    if (!pid) {
      setPendingProductThumb(null);
      return;
    }
    const cached = getCachedProductThumbnailSrc(pid);
    setPendingProductThumb(cached);
  }, [open, pendingConversationTarget?.productId]);

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 400,
          maxWidth: '90vw',
          boxShadow: theme.shadows[16],
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          p: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MessageIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Messages
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={showDebugInfo ? "Hide debug info" : "Show debug info"}>
              <IconButton 
                onClick={() => setShowDebugInfo(!showDebugInfo)} 
                size="small"
                sx={{ color: showDebugInfo ? 'primary.main' : 'text.secondary' }}
              >
                {showDebugInfo ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* User Information Display - Beautiful and Efficient */}
        <Box 
          sx={{ 
            p: 2, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.92)} 0%, ${alpha(theme.palette.primary.dark, 0.92)} 100%)`,
            borderRadius: 2,
            boxShadow: theme.shadows[4],
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5, fontWeight: 500 }}>
                {user?.name || 'No name set'}
              </Typography>
              {loadingAlias ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Loading alias...
                  </Typography>
                </Box>
              ) : (
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  {userAlias || 'No alias set'}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.3)'
              }}
            >
              {pendingProductThumb ? (
                <Box
                  component="img"
                  src={pendingProductThumb}
                  alt={pendingConversationTarget?.subject || 'Product'}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <ProductIcon sx={{ color: 'white', fontSize: 26 }} />
              )}
            </Box>
          </Box>
          
          {/* Pending Conversation Target Info */}
          {pendingConversationTarget && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Pending Conversation:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Product ID: {pendingConversationTarget.productId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Receiver ID: {pendingConversationTarget.receiverId}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Subject: {pendingConversationTarget.subject}
              </Typography>
            </Box>
          )}
          
          {/* Debug Information */}
          {showDebugInfo && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Debug Information:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
                Current User ID: {user?.id || 'Unknown'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
                Conversations Count: {conversations.length}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
                Highlighted Conv: {highlightConversationId || 'None'}
              </Typography>
              {pendingConversationTarget && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace' }}>
                    Pending Product ID: {pendingConversationTarget.productId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                    Pending Receiver ID: {pendingConversationTarget.receiverId}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        {/* Conversations List */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary', px: 1 }}>
            Conversations ({conversations.length})
          </Typography>
          
          {loadingConversations ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                Loading conversations...
              </Typography>
            </Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <ChatIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No conversations yet
              </Typography>
            </Box>
          ) : (
            <List sx={{ 
              p: 0, 
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.primary.main, 0.3),
                borderRadius: '4px',
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.5),
                },
              },
            }}>
              {conversations.map((conversation, index) => {
                const chatPartner = getChatPartner(conversation);
                const partnerName = chatPartner?.alias || chatPartner?.name || `User ${chatPartner?.id?.substring(0, 8)}...`;
                const productId = String((conversation as any)?.product_id || conversation.product?.id || '').trim();
                const cachedThumb = productId ? getCachedProductThumbnailSrc(productId) : null;
                const fallbackThumb = (conversation as any)?.product?.image_url || (conversation as any)?.product?.images?.[0];
                const thumbSrc = normalizeProductImageSrc(cachedThumb) || normalizeProductImageSrc(fallbackThumb);
                const messageCount = typeof (conversation as any)?.message_count === 'number'
                  ? (conversation as any).message_count
                  : undefined;
                // Format last message nicely (handle offer/image/etc.)
                const lm: any = conversation.last_message as any;
                const lmType = String(lm?.message_type || '').trim();
                const lmContent = String(lm?.content || '').trim();

                let lastMessage = 'No messages yet';
                if (lmContent) {
                  lastMessage = lmContent;
                  if (lastMessage.length > 80) lastMessage = lastMessage.slice(0, 80) + '...';
                  if (lastMessage.startsWith('NEW_OFFER')) {
                    const parts = lastMessage.split('|');
                    const price = parts[1];
                    const note = parts[2];
                    lastMessage = `Offer${price ? `: $${price}` : ''}${note ? ` — ${note}` : ''}`;
                  }
                } else if (lmType === 'image' || lmType === 'photo' || !!lm?.image_url || !!lm?.image_path) {
                  lastMessage = 'Photo';
                } else if (lmType === 'document' || !!lm?.document_url || !!lm?.document_path) {
                  lastMessage = 'Document';
                } else if (lmType === 'video' || !!lm?.video_url || !!lm?.video_path) {
                  lastMessage = 'Video';
                } else if (lmType === 'offer' || (lmContent || '').startsWith('NEW_OFFER')) {
                  lastMessage = 'Offer';
                } else if (lm?.id) {
                  // We have a last_message object but no content/type; show a neutral label
                  lastMessage = 'Message';
                }
                const lastMessageTime = conversation.last_message?.created_at ? formatTime(conversation.last_message.created_at) : '';
                const isLastMessageFromPartner = conversation.last_message?.sender_id !== user?.id;

                const isHighlighted = highlightConversationId === conversation.id || (pendingConversationTarget && conversation.product_id === pendingConversationTarget.productId);

                return (
                  <React.Fragment key={conversation.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          try {
                            setPendingConversationTarget(undefined);
                          } catch {}
                          setHighlightConversationId(conversation.id);
                          navigate(`/messages?conversation=${conversation.id}`);
                          onClose();
                        }}
                        sx={{
                          py: 2,
                          px: 2,
                          borderRadius: 1,
                          mx: 1,
                          my: 0.5,
                          transition: 'all 0.2s ease-in-out',
                          bgcolor: isHighlighted ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                          border: isHighlighted ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}` : '1px solid transparent',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            transform: 'translateX(2px)',
                          },
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 92, mr: 1.75 }}>
                          {(() => {
                            return (
                              <Box sx={{ position: 'relative', width: 76, height: 76 }}>
                                <Box
                                  sx={{
                                    width: 76,
                                    height: 76,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: '2px solid',
                                    borderColor: 'divider',
                                    boxShadow: theme.shadows[2],
                                    bgcolor: 'grey.200',
                                  }}
                                >
                                  {thumbSrc ? (
                                    <Box
                                      component="img"
                                      src={thumbSrc}
                                      alt={conversation.product?.name || conversation.subject || 'Product'}
                                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                  ) : (
                                    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <ProductIcon sx={{ color: 'text.secondary' }} />
                                    </Box>
                                  )}
                                </Box>

                                {typeof messageCount === 'number' && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      left: -6,
                                      bottom: -6,
                                      minWidth: 22,
                                      height: 22,
                                      px: 0.75,
                                      borderRadius: 999,
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.75rem',
                                      fontWeight: 900,
                                      boxShadow: theme.shadows[3],
                                      border: '2px solid',
                                      borderColor: 'background.paper',
                                    }}
                                  >
                                    {messageCount}
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {partnerName}
                                </Typography>
                                {conversation.subject && (
                                  <Typography 
                                    variant="body2" 
                                    color="primary.main" 
                                    sx={{ 
                                      fontWeight: 600, 
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {conversation.subject}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontWeight: 500 }}>
                                {lastMessageTime}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            <Box>
                              {showDebugInfo && (
                                <Box sx={{ mb: 0.75 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.62rem' }}>
                                    productId: {productId || '—'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.62rem' }}>
                                    cachedThumb: {(cachedThumb ? String(cachedThumb).slice(0, 60) : '—')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.62rem' }}>
                                    fallbackThumb: {(fallbackThumb ? String(fallbackThumb).slice(0, 60) : '—')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.62rem' }}>
                                    thumbSrc: {(thumbSrc ? String(thumbSrc).slice(0, 60) : '—')}
                                  </Typography>
                                </Box>
                              )}
                              {conversation.product && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Chip
                                    icon={<ProductIcon sx={{ fontSize: 12 }} />}
                                    label={conversation.product.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ 
                                      height: 22, 
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      '& .MuiChip-icon': { fontSize: 12 }
                                    }}
                                  />
                                  {conversation.product.price && (
                                    <Chip
                                      icon={<PriceIcon sx={{ fontSize: 12 }} />}
                                      label={`$${conversation.product.price.toLocaleString()}`}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ 
                                        height: 22, 
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        '& .MuiChip-icon': { fontSize: 12 }
                                      }}
                                    />
                                  )}
                                </Box>
                              )}
                              
                              {/* Debug Info - Only show if enabled */}
                              {showDebugInfo && (
                                <Box sx={{ mb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                  <Tooltip title={`Conversation ID: ${conversation.id}`} arrow>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', cursor: 'help' }}>
                                      Conv: {conversation.id.substring(0, 8)}...
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={`User 1 ID: ${conversation.user1_id}`} arrow>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', cursor: 'help' }}>
                                      U1: {conversation.user1_id.substring(0, 8)}...
                                    </Typography>
                                  </Tooltip>
                                  <Tooltip title={`User 2 ID: ${conversation.user2_id}`} arrow>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', cursor: 'help' }}>
                                      U2: {conversation.user2_id.substring(0, 8)}...
                                    </Typography>
                                  </Tooltip>
                                </Box>
                              )}
                              
                              {/* Last Message */}
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography 
                                  variant="body2" 
                                  color={isLastMessageFromPartner ? 'text.primary' : 'text.secondary'}
                                  sx={{ 
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: isLastMessageFromPartner ? 500 : 400
                                  }}
                                >
                                  {isLastMessageFromPartner ? '' : 'You: '}{lastMessage}
                                </Typography>
                                {isLastMessageFromPartner && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      flexShrink: 0
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < conversations.length - 1 && (
                      <Divider variant="inset" component="li" sx={{ ml: 11.25 }} />
                    )}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default MessagesSidebar;
